/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import * as DSAssetFamilies from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { ENUMERATION, NUMBER } from 'customer-data-objects/property/PropertyTypes';
import { List } from 'immutable';
import { useCallback, useContext } from 'react';
import getIn from 'transmute/getIn';
import { getDefaultAssociation } from 'customer-data-filters-ui/strategy/InboundDbListSegStrategyUtils';
import { ASSOCIATION_DEFINITION } from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import FilterUserAction from 'customer-data-filters-ui/filterQueryFormat/FilterUserAction';
import { addCondition, addToKeyPath, getExactKeyPath, getKeyPath, getRealPath, removeEmptyChildGroups, removeKeyPath, setKeyPath } from 'customer-data-filters-ui/filterQueryFormat/logic/LogicGroup';
import EmailLinkClicked from 'customer-data-filters-ui/filterQueryFormat/operator/EmailLinkClicked';
import { isOperator } from 'customer-data-filters-ui/filterQueryFormat/operator/Operator';
import { USED_SEGMENTATION_FILTERS } from 'customer-data-filters-ui/filterQueryFormat/UsageActionTypes';
import { CONDITION_DELETED, EDITOR_CONDITION_ADDED } from 'customer-data-filters-ui/filterQueryFormat/UsageSubActionTypes';
import SegmentationFiltersInstanceContext from '../state/SegmentationFiltersInstanceContext';
import { isConditionsPseudoGroup, isEventBasedFilterFamily, shouldAddEventPageRefinement } from '../strategy/eventBasedFilters';
import { useGetOperators } from './useGetOperators';
import { useLoadMoreFieldsHandler } from './useLoadMoreFieldsHandler';
import { useOnChangeHandlers } from './useOnChangeHandlers';
import { useStrategy } from './useStrategy';
import { findDeepConditionKeyPath } from 'customer-data-filters-ui/filterQueryFormat/logic/LogicGroup';
import { And } from '../filterQueryFormat/logic/FilterLogicGroup';
import invariant from 'react-utils/invariant';
import { isPropertyChangedEventTypeId } from '../strategy/propertyChangedFilterUtils';
import PropertyNameAndValue from '../filterQueryFormat/operator/PropertyNameAndValue';
import PropertyChangedNameAndValueField from '../converters/objectSeg/fieldDefinitions/PropertyChangedNameAndValueField';
import { useValidateCondition } from './useValidateCondition';
import { EQUATION, ROLLUP } from 'customer-data-objects/property/PropertyFieldTypes';
import { operatorsWithPropertyHistoryRequirement } from './useOperatorOptions';
import { isRelativeTimeProperty } from '../utils/isRelativeTimeProperty';
import { DATE_REFINEMENT, NUMBER_REFINEMENT } from 'customer-data-filters-ui/converters/objectSeg/constants/constants';
import getConditionId from '../utils/getConditionId';
import { useViewerActions } from './useViewerActions';
import { ObjectTypeIdRegex, isAnyEventTypeId } from 'customer-data-filters-ui/filterQueryFormat/ObjectTypeId';
export const utils = {
  getEventBasedFilterFamilyCondition: (filterFamily, field, getOperators, getRefinementOperators, conditionToRefineKeyPath, internalDraftValue) => {
    const existingCondition = conditionToRefineKeyPath ? internalDraftValue.getIn(getRealPath(List(conditionToRefineKeyPath))) : undefined;
    let condition;

    // use existing condition if `conditionToRefineKeyPath` is specified
    // (e.g. user clicked refine form submission status filter)
    if (existingCondition && isOperator(existingCondition)) {
      condition = existingCondition;
    } else {
      // otherwise create new condition
      const operators = getOperators(filterFamily, field);
      condition = operators.first().of(field);
    }

    // add refinements, either event date or event count filter but not both
    if (field.refinement) {
      const refinementOperators = getRefinementOperators(filterFamily, field);
      const refinementFieldName = field.type === NUMBER ? NUMBER_REFINEMENT : DATE_REFINEMENT;
      condition = condition.set(refinementFieldName, refinementOperators.first().of(field));
    }

    // add event page filter (e.g. page on which form was submitted)
    if (shouldAddEventPageRefinement(filterFamily, field.name)) {
      condition = condition.set('isPageSelected', true);
    }
    return condition;
  },
  getCondition: (filterFamily, field, getOperators, getRefinementOperators, conditionToRefineKeyPath, internalDraftValue, data) => {
    if (isEventBasedFilterFamily(filterFamily)) {
      return utils.getEventBasedFilterFamilyCondition(filterFamily, field, getOperators, getRefinementOperators, conditionToRefineKeyPath, internalDraftValue,
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 6 arguments, but got 7.
      data);
    }
    const operators = getOperators(filterFamily, field);
    return operators.first().of(field);
  },
  addAssociationToChildFilterGroup: (filterGroup, childFilterGroupPath, associationDefinitions) => {
    const filterFamily = filterGroup.getIn(getRealPath(childFilterGroupPath).push('filterFamily'));
    const parentFilterFamily = filterGroup.getIn(getRealPath(childFilterGroupPath.pop()).push('filterFamily'));
    const defaultAssociation = getDefaultAssociation(parentFilterFamily, filterFamily, associationDefinitions);
    if (defaultAssociation) {
      return filterGroup.mergeIn(getRealPath(childFilterGroupPath), {
        associationTypeId: defaultAssociation.id,
        associationCategory: defaultAssociation.category
      });
    }
    return filterGroup;
  },
  addConditionWrappedWithAssociationBranch: (condition, parentGroup, filterFamily, associationFilterFamily) => {
    const associationBranchWithCondition = And.of(addCondition(condition, And.of().set('filterFamily', filterFamily))).set('filterFamily', associationFilterFamily);
    return addCondition(associationBranchWithCondition, parentGroup);
  },
  addConditionToFilterGroup: (condition, filterFamily, isNewSameObjectAssociationBranch, filterGroup, conditionToRefineKeyPath, associationDefinitions, associationFilterFamily) => {
    // replace existing condition if `conditionToRefineKeyPath` is specified
    // (e.g. user clicked refine form submission status filter)
    if (isConditionsPseudoGroup(filterFamily) && conditionToRefineKeyPath) {
      return setKeyPath(List(conditionToRefineKeyPath), condition, filterGroup);
    }
    const conditionToAdd = isNewSameObjectAssociationBranch ? And.of(condition).set('filterFamily', filterFamily) : condition;

    // otherwise use standard `addToKeyPath` logic
    const newFilterGroup = associationFilterFamily ? utils.addConditionWrappedWithAssociationBranch(conditionToAdd, filterGroup, filterFamily, associationFilterFamily) : addToKeyPath(List(), conditionToAdd, filterGroup, filterFamily);
    const conditionPath = findDeepConditionKeyPath(condition, newFilterGroup);
    // if a condition was placed in a new subgroup add association data
    if (conditionPath.length > 1) {
      return utils.addAssociationToChildFilterGroup(newFilterGroup, conditionPath.setSize(1), associationDefinitions);
    }
    return newFilterGroup;
  },
  getIsValuePreservable: (filterFamily, currentValue, newValue, getOperators, getValueResolver) => {
    var _newValue$field;
    const oldValueType = getIn(['field', 'type'], currentValue);
    const newValueType = getIn(['field', 'type'], newValue);
    const validOperators = getOperators(filterFamily, newValue.field);

    // HACK: CTA's are special because they use a custom input that fetches
    // different options for each CTA. This means they can never be preserved.
    if (filterFamily === DSAssetFamilies.CTA) {
      return false;
    }

    // HACK: Similar to CTAs Email Links use a custom input that does a different
    // fetch per email selected so they can't be persisted.
    if (currentValue.constructor === EmailLinkClicked) {
      return false;
    }

    /* In general, value cannot be preserved when switching to/from relative time properties.
     * Value can be preserved iff both current and new property are relative time properties of the same type.
     */
    const isCurrentPropertyRelativeTimeProperty = isRelativeTimeProperty(currentValue.field);
    const isNewPropertyRelativeTimeProperty = isRelativeTimeProperty(newValue.field);
    if (isCurrentPropertyRelativeTimeProperty || isNewPropertyRelativeTimeProperty) {
      if (isCurrentPropertyRelativeTimeProperty && isNewPropertyRelativeTimeProperty && oldValueType === newValueType) {
        return true;
      }
      return false;
    }

    // Calculated properties cannot use historical operators, so these operators can't be persisted
    const isFieldCalculatedProperty = [EQUATION, ROLLUP].includes((_newValue$field = newValue.field) === null || _newValue$field === void 0 ? void 0 : _newValue$field.fieldType);
    if (isFieldCalculatedProperty && operatorsWithPropertyHistoryRequirement.includes(currentValue.constructor)) {
      return false;
    }
    if (!validOperators.includes(currentValue.constructor)) {
      return false;
    }
    if (!currentValue.value) {
      return true;
    }

    // Enumeration properties that are not fetched externally will have options
    // on the object to compare to.
    if (!getValueResolver(filterFamily, currentValue) && !getValueResolver(filterFamily, newValue) && oldValueType === ENUMERATION && newValueType === ENUMERATION) {
      const newOptions = getIn(['field', 'options'], newValue) || List();
      if (currentValue.constructor.isIterableField('value')) {
        return currentValue.value.every(value => newOptions.findIndex(option => option.value === value) !== -1);
      }
      return newOptions.findIndex(option => option.value === currentValue.value) !== -1;
    }
    return getValueResolver(filterFamily, currentValue) === getValueResolver(filterFamily, newValue);
  },
  getNewConditionForChangedField: (filterFamily, condition, newField, getOperators, getRefinementOperators, getValueResolver, internalDraftValue, data) => {
    const maybeNewCondition = condition.set('field', newField);
    const isValuePreservable = utils.getIsValuePreservable(filterFamily, condition, maybeNewCondition, getOperators, getValueResolver);
    if (isValuePreservable) {
      return maybeNewCondition;
    } else {
      return utils.getCondition(filterFamily, newField, getOperators, getRefinementOperators, undefined, internalDraftValue, data);
    }
  }
};
export const useEditorActions = () => {
  const {
    dispatch,
    internalDraftValue,
    draftCondition,
    conditionToRefineKeyPath,
    conditionPathToChangeFieldFor,
    data,
    getTrackerInstance,
    selectedFilterFamily,
    selectedObjectType: selectedObjectTypeState,
    props: {
      onUserAction,
      baseFilterFamily,
      useOnePanel
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getOperators,
    getRefinementOperators
  } = useGetOperators();
  const {
    handleOnChange
  } = useOnChangeHandlers();
  const {
    getValueResolver,
    getRequiredConditionForEvent
  } = useStrategy();
  const loadMoreFields = useLoadMoreFieldsHandler();
  const {
    validateLogicGroup
  } = useValidateCondition();
  const {
    onHighlightCondition
  } = useViewerActions();
  const getAssociationFilterFamily = useCallback(filterFamily => {
    const selectedObjectType = selectedObjectTypeState || {
      filterFamily: baseFilterFamily,
      isObjectOwnFilterBranch: true
    };
    const shouldWrapWithAssociationBranch =
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    draftCondition.conditionPath.size <= 1 && !selectedObjectType.isObjectOwnFilterBranch && filterFamily !== selectedObjectType.filterFamily;
    return shouldWrapWithAssociationBranch ? selectedObjectType.filterFamily : undefined;
  }, [baseFilterFamily, draftCondition, selectedObjectTypeState]);
  const onChangeInternal = useCallback(newInternalDraftValue => {
    dispatch({
      type: 'internalDraftValue',
      payload: newInternalDraftValue
    });
    handleOnChange(newInternalDraftValue);
  }, [dispatch, handleOnChange]);
  const getNewValueForRefinedCondition = useCallback((filterFamily, field) => {
    const newCondition = utils.getCondition(filterFamily, field, getOperators, getRefinementOperators, conditionToRefineKeyPath, internalDraftValue, data);
    return {
      conditionPath: conditionToRefineKeyPath,
      newInternalDraftValue: setKeyPath(conditionToRefineKeyPath, newCondition, internalDraftValue)
    };
  }, [conditionToRefineKeyPath, data, getOperators, getRefinementOperators, internalDraftValue]);
  const getNewValueForChangedFieldCondition = useCallback((filterFamily, field) => {
    const existingCondition = getExactKeyPath(conditionPathToChangeFieldFor, internalDraftValue);
    const newCondition = utils.getNewConditionForChangedField(filterFamily, existingCondition, field, getOperators, getRefinementOperators, getValueResolver, internalDraftValue, data);
    return {
      conditionPath: conditionPathToChangeFieldFor,
      newInternalDraftValue: setKeyPath(conditionPathToChangeFieldFor, newCondition, internalDraftValue)
    };
  }, [conditionPathToChangeFieldFor, data, internalDraftValue, getOperators, getRefinementOperators, getValueResolver]);
  const getNewValueForExistingCondition = useCallback((filterFamily, field) => {
    if (conditionToRefineKeyPath) {
      return getNewValueForRefinedCondition(filterFamily, field);
    }
    if (conditionPathToChangeFieldFor) {
      return getNewValueForChangedFieldCondition(filterFamily, field);
    }
    throw new Error('`conditionToRefineKeyPath` or `conditionPathToChangeFieldFor` is not specified');
  }, [conditionToRefineKeyPath, conditionPathToChangeFieldFor, getNewValueForChangedFieldCondition, getNewValueForRefinedCondition]);
  const getNewValueForNewCondition = useCallback((filterFamily, field) => {
    invariant(
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    draftCondition.isAddingNew, '`draftCondition.isAddingNew` expected to be `true` when adding new condition');
    invariant(
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    filterFamily === selectedFilterFamily.filterFamily, '`filterFamily` provided as an argument and `filterFamily` in the state should match');
    const condition = utils.getCondition(filterFamily, field, getOperators, getRefinementOperators, conditionToRefineKeyPath, internalDraftValue, data);
    const conditionFilterGroup = getExactKeyPath(
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    draftCondition.conditionPath, internalDraftValue);
    if (!conditionFilterGroup) {
      throw new Error(`Filter group not found at the location ${
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      draftCondition.conditionPath} in ${JSON.stringify(
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      internalDraftValue.toJS())}`);
    }
    const isNewSameObjectAssociationBranch = filterFamily === baseFilterFamily &&
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    !selectedFilterFamily.isObjectOwnFilterBranch &&
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    draftCondition.conditionPath.size < 2;
    const conditionFilterGroupWithCondition = utils.addConditionToFilterGroup(condition, filterFamily, isNewSameObjectAssociationBranch, conditionFilterGroup, conditionToRefineKeyPath, data.get(ASSOCIATION_DEFINITION), getAssociationFilterFamily(filterFamily));
    const newInternalDraftValue = setKeyPath(
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    draftCondition.conditionPath, conditionFilterGroupWithCondition, internalDraftValue);
    return {
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      conditionPath: draftCondition.conditionPath.concat(findDeepConditionKeyPath(condition, conditionFilterGroupWithCondition)),
      newInternalDraftValue
    };
  }, [conditionToRefineKeyPath, data, draftCondition, internalDraftValue, getAssociationFilterFamily, getOperators, getRefinementOperators, baseFilterFamily, selectedFilterFamily]);
  const selectField = useCallback((filterFamily, field, usedSemanticSearch, hadSemanticSearchResults) => {
    const {
      conditionPath,
      newInternalDraftValue
    } = conditionPathToChangeFieldFor || conditionToRefineKeyPath ? getNewValueForExistingCondition(filterFamily, field) : getNewValueForNewCondition(filterFamily, field);
    const validatedNewInternalDraftValue = validateLogicGroup(newInternalDraftValue, false);
    dispatch({
      type: 'selectField',
      payload: {
        filterFamily,
        conditionPath,
        internalDraftValue: validatedNewInternalDraftValue
      }
    });
    onChangeInternal(validatedNewInternalDraftValue);
    if (useOnePanel) {
      onHighlightCondition(getConditionId(conditionPath));
    }

    // if its a property on an object or event, use the property name
    // otherwise, use the filter family otherwise we get the form or email id, etc...
    const propertyOrFilterFamily = ObjectTypeIdRegex.test(filterFamily) ? field.name : filterFamily;
    if (conditionPathToChangeFieldFor) {
      var _getTrackerInstance;
      (_getTrackerInstance = getTrackerInstance()) === null || _getTrackerInstance === void 0 || _getTrackerInstance.track('changedFilterType', {
        primaryObjectTypeId: baseFilterFamily,
        property: propertyOrFilterFamily,
        usedSemanticSearch,
        hadSemanticSearchResults,
        version: useOnePanel ? '2' : '1'
      });
    } else {
      var _getTrackerInstance2;
      (_getTrackerInstance2 = getTrackerInstance()) === null || _getTrackerInstance2 === void 0 || _getTrackerInstance2.track('addedCondition', {
        primaryObjectTypeId: baseFilterFamily,
        property: propertyOrFilterFamily,
        usedSemanticSearch,
        hadSemanticSearchResults,
        version: useOnePanel ? '2' : '1'
      });
    }
  }, [conditionPathToChangeFieldFor, conditionToRefineKeyPath, getNewValueForExistingCondition, getNewValueForNewCondition, validateLogicGroup, dispatch, onChangeInternal, useOnePanel, onHighlightCondition, getTrackerInstance, baseFilterFamily]);
  const addFilter = useCallback(() => {
    dispatch({
      type: 'fieldSelectMode',
      payload: true
    });
    onUserAction(FilterUserAction({
      action: USED_SEGMENTATION_FILTERS,
      subAction: EDITOR_CONDITION_ADDED
    }));
  }, [dispatch, onUserAction]);
  const onConditionDelete = useCallback(conditionPath => {
    var _getTrackerInstance3;
    const conditionToDeleteParentGroup = getKeyPath(conditionPath.skipLast(1), internalDraftValue);
    const removeEmptyParentGroup = !isAnyEventTypeId(conditionToDeleteParentGroup.filterFamily);
    const newInternalDraftValue = validateLogicGroup(removeKeyPath(conditionPath, removeEmptyChildGroups(internalDraftValue), removeEmptyParentGroup));
    dispatch({
      type: 'deleteCondition',
      payload: {
        internalDraftValue: newInternalDraftValue
      }
    });
    handleOnChange(newInternalDraftValue);
    (_getTrackerInstance3 = getTrackerInstance()) === null || _getTrackerInstance3 === void 0 || _getTrackerInstance3.track('deletedCondition', {
      primaryObjectTypeId: baseFilterFamily,
      version: useOnePanel ? '2' : '1'
    });
    onUserAction(FilterUserAction({
      action: USED_SEGMENTATION_FILTERS,
      subAction: CONDITION_DELETED
    }));
  }, [internalDraftValue, validateLogicGroup, dispatch, handleOnChange, getTrackerInstance, onUserAction, baseFilterFamily, useOnePanel]);
  const onRefineFilter = useCallback((filterFamily, conditionPath) => {
    dispatch({
      type: 'refineFilter',
      payload: {
        conditionToRefineKeyPath: conditionPath,
        filterFamilyInfo: {
          filterFamily,
          isObjectOwnFilterBranch: false
        }
      }
    });

    // @ts-expect-error ts-migrate(2554) FIXME: Expected 3 arguments, but got 1.
    loadMoreFields(filterFamily).then(() => dispatch({
      type: 'fieldsLoaded'
    }));
  }, [dispatch, loadMoreFields]);
  const setCategory = useCallback(category => {
    dispatch({
      type: 'setCategory',
      payload: {
        category
      }
    });
  }, [dispatch]);
  const addEmptyFilterGroup = useCallback(filterFamily => {
    var _getTrackerInstance4;
    const parentFilterGroup = getExactKeyPath(
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    draftCondition.conditionPath, internalDraftValue);
    if (!parentFilterGroup) {
      throw new Error(`Filter group not found at the location ${
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      draftCondition.conditionPath} in ${JSON.stringify(
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      internalDraftValue.toJS())}`);
    }
    const associationFilterFamily = getAssociationFilterFamily(filterFamily);
    const emptyUnifiedEventGroup = And.of().set('filterFamily', filterFamily);
    let newFilterGroup = associationFilterFamily ? addCondition(And.of(emptyUnifiedEventGroup).set('filterFamily', associationFilterFamily), parentFilterGroup) : addCondition(emptyUnifiedEventGroup, parentFilterGroup);
    const conditionPath = findDeepConditionKeyPath(emptyUnifiedEventGroup, newFilterGroup);
    if (conditionPath.length > 1) {
      newFilterGroup = utils.addAssociationToChildFilterGroup(newFilterGroup, conditionPath.setSize(1), data.get(ASSOCIATION_DEFINITION));
    }
    const newInternalDraftValue = setKeyPath(
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    draftCondition.conditionPath, newFilterGroup, internalDraftValue);
    dispatch({
      type: 'resetEditor',
      payload: {
        internalDraftValue: newInternalDraftValue,
        shouldLeaveEditorOpen: true
      }
    });
    handleOnChange(newInternalDraftValue);
    (_getTrackerInstance4 = getTrackerInstance()) === null || _getTrackerInstance4 === void 0 || _getTrackerInstance4.track('addedCondition', {
      primaryObjectTypeId: baseFilterFamily,
      property: filterFamily,
      version: useOnePanel ? '2' : '1'
    });
    const payload = {
      conditionPath: findDeepConditionKeyPath(emptyUnifiedEventGroup, newInternalDraftValue),
      filterFamily
    };
    dispatch({
      type: 'editFilter',
      payload
    });
    return payload.conditionPath;
  }, [draftCondition, internalDraftValue, getAssociationFilterFamily, dispatch, handleOnChange, getTrackerInstance, data, baseFilterFamily, useOnePanel]);
  const addPropertyValueChangedFilterGroup = useCallback(filterFamily => {
    var _getTrackerInstance5;
    if (!draftCondition || !draftCondition.conditionPath) {
      throw new Error(`draftCondition.conditionPath should be defined`);
    }
    if (!internalDraftValue) {
      throw new Error(`internalDraftValue should be defined`);
    }
    const parentFilterGroup = getExactKeyPath(draftCondition.conditionPath, internalDraftValue);
    if (!parentFilterGroup) {
      throw new Error(`Filter group not found at the location ${draftCondition.conditionPath} in ${JSON.stringify(internalDraftValue.toJS())}`);
    }
    const propertyNameAndValueCondition = PropertyNameAndValue.of(PropertyChangedNameAndValueField());
    const newParentGroupWithPropertyNameAndValueGroup = addCondition(propertyNameAndValueCondition, parentFilterGroup, filterFamily);
    const newInternalDraftValue = setKeyPath(draftCondition.conditionPath, newParentGroupWithPropertyNameAndValueGroup, internalDraftValue);
    const propertyNameAndValueConditionPath = draftCondition.conditionPath.concat(findDeepConditionKeyPath(propertyNameAndValueCondition, newParentGroupWithPropertyNameAndValueGroup));
    dispatch({
      type: 'selectField',
      payload: {
        filterFamily,
        conditionPath: propertyNameAndValueConditionPath,
        internalDraftValue: newInternalDraftValue
      }
    });
    handleOnChange(newInternalDraftValue);
    (_getTrackerInstance5 = getTrackerInstance()) === null || _getTrackerInstance5 === void 0 || _getTrackerInstance5.track('addedCondition', {
      primaryObjectTypeId: baseFilterFamily,
      property: filterFamily,
      version: useOnePanel ? '2' : '1'
    });
    return propertyNameAndValueConditionPath;
  }, [draftCondition, internalDraftValue, dispatch, handleOnChange, getTrackerInstance, baseFilterFamily, useOnePanel]);
  const addFilterGroupWithRequiredCondition = useCallback((filterFamily, requiredCondition) => {
    if (!draftCondition || !draftCondition.conditionPath) {
      throw new Error(`draftCondition.conditionPath should be defined`);
    }
    if (!internalDraftValue) {
      throw new Error(`internalDraftValue should be defined`);
    }
    const parentFilterGroup = getExactKeyPath(draftCondition.conditionPath, internalDraftValue);
    if (!parentFilterGroup) {
      throw new Error(`Filter group not found at the location ${draftCondition.conditionPath} in ${JSON.stringify(internalDraftValue.toJS())}`);
    }
    const newParentGroupWithRequiredCondition = addCondition(requiredCondition, parentFilterGroup, filterFamily);
    const newInternalDraftValue = setKeyPath(draftCondition.conditionPath, newParentGroupWithRequiredCondition, internalDraftValue);
    const requiredConditionPath = draftCondition.conditionPath.concat(findDeepConditionKeyPath(requiredCondition, newParentGroupWithRequiredCondition));
    dispatch({
      type: 'selectField',
      payload: {
        filterFamily,
        conditionPath: requiredConditionPath,
        internalDraftValue: newInternalDraftValue
      }
    });
    handleOnChange(newInternalDraftValue);
    return requiredConditionPath;
  }, [draftCondition, internalDraftValue, dispatch, handleOnChange]);
  const selectFilterFamily = useCallback(async (filterFamily, isAssociation = false) => {
    if (isAnyEventTypeId(filterFamily)) {
      if (isPropertyChangedEventTypeId(filterFamily)) {
        const conditionPath = addPropertyValueChangedFilterGroup(filterFamily);
        if (useOnePanel) {
          onHighlightCondition(getConditionId(conditionPath));
        }
        return;
      }
      const requiredCondition = await getRequiredConditionForEvent(filterFamily);
      // TODO: only add this filter if not present in filter value
      if (requiredCondition) {
        const conditionPath = addFilterGroupWithRequiredCondition(filterFamily, requiredCondition);
        if (useOnePanel) {
          onHighlightCondition(getConditionId(conditionPath));
        }
        return;
      }
      const conditionPath = addEmptyFilterGroup(filterFamily);
      if (useOnePanel) {
        onHighlightCondition(getConditionId(conditionPath));
      }
    } else {
      dispatch({
        type: 'filterFamily',
        payload: {
          filterFamilyInfo: {
            filterFamily,
            isObjectOwnFilterBranch: filterFamily === baseFilterFamily && !isAssociation
          }
        }
      });

      // @ts-expect-error ts-migrate(2554) FIXME: Expected 3 arguments, but got 1.
      loadMoreFields(filterFamily).then(() => dispatch({
        type: 'fieldsLoaded'
      }));
    }
  }, [addPropertyValueChangedFilterGroup, addEmptyFilterGroup, useOnePanel, onHighlightCondition, dispatch, baseFilterFamily, loadMoreFields, addFilterGroupWithRequiredCondition, getRequiredConditionForEvent]);
  const selectFilterFamilyGroup = useCallback(filterFamilyGroup => {
    dispatch({
      type: 'filterFamilyGroup',
      payload: {
        filterFamilyGroup
      }
    });
  }, [dispatch]);
  const changeField = useCallback((filterFamily, conditionPath) => {
    dispatch({
      type: 'changeField',
      payload: {
        filterFamilyInfo: {
          filterFamily,
          isObjectOwnFilterBranch: baseFilterFamily === filterFamily && conditionPath.size === 2
        },
        conditionPath
      }
    });

    // @ts-expect-error ts-migrate(2554) FIXME: Expected 3 arguments, but got 1.
    loadMoreFields(filterFamily).then(() => dispatch({
      type: 'fieldsLoaded'
    }));
  }, [dispatch, loadMoreFields, baseFilterFamily]);
  const onConditionSelectCancel = useCallback(() => {
    dispatch({
      type: 'resetEditor',
      payload: {
        shouldLeaveEditorOpen: true,
        internalDraftValue: removeEmptyChildGroups(internalDraftValue)
      }
    });
  }, [dispatch, internalDraftValue]);
  const onCloseEditor = useCallback(() => {
    dispatch({
      type: 'hideEditor'
    });
  }, [dispatch]);
  const resetFilterCategorySelect = useCallback((resetSelectedFilterCategoryGroup = false) => {
    dispatch({
      type: 'resetSelectedFilterCategory',
      payload: {
        resetSelectedFilterCategoryGroup
      }
    });
  }, [dispatch]);
  const onObjectTypeSelect = useCallback(selectedObjectType => {
    var _getTrackerInstance6;
    dispatch({
      type: 'selectedObjectType',
      payload: selectedObjectType
    });
    (_getTrackerInstance6 = getTrackerInstance()) === null || _getTrackerInstance6 === void 0 || _getTrackerInstance6.track('changedObjectType', {
      primaryObjectTypeId: baseFilterFamily,
      objectType: selectedObjectType.filterFamily,
      type: selectedObjectType.isObjectOwnFilterBranch ? 'current' : 'associated',
      version: useOnePanel ? '2' : '1'
    });
  }, [baseFilterFamily, dispatch, getTrackerInstance, useOnePanel]);
  return {
    addFilter,
    setCategory,
    selectField,
    onChangeInternal,
    onConditionDelete,
    onRefineFilter,
    selectFilterFamily,
    selectFilterFamilyGroup,
    changeField,
    onConditionSelectCancel,
    onCloseEditor,
    resetFilterCategorySelect,
    onObjectTypeSelect
  };
};